import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Calendar, ChevronRight, Lock } from 'lucide-react';
import { toast } from 'sonner';
import AutoTranslatedText from '../AutoTranslatedText';
import { parseLocalDate } from '../../utils/dateUtils';

const BuyerEventsTab = ({
  events,
  myRegistrations,
  currentLang,
  formatPrice,
  t,
  setSelectedEvent,
  loadExhibitors,
  setActiveTab,
  setEventToRegister,
  setShowRegisterModal,
}) => {
  const isRegisteredForEvent = (eventId) => myRegistrations.some(r => r.event_id === eventId);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('dashboard.available_events')}</CardTitle>
        <CardDescription>{t('dashboard.register_events_desc')}</CardDescription>
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">{t('dashboard.no_events_available')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {events.map((event) => (
              <div key={event.id} className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="font-semibold text-lg">{event.name}</h3>
                      <Badge className={
                        event.status === 'live' ? 'bg-green-100 text-green-700' :
                        event.status === 'registration_open' ? 'bg-amber-100 text-amber-700' :
                        'bg-blue-100 text-blue-700'
                      }>
                        {event.status === 'registration_open' ? t('dashboard.registration_open_status') : event.status?.toUpperCase()}
                      </Badge>
                      {isRegisteredForEvent(event.id) && (
                        <Badge className="bg-primary/10 text-primary">{t('dashboard.registered')}</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mb-2">
                      <AutoTranslatedText text={event.description} translations={event.description_translations} showLoading={false} />
                    </p>
                    <div className="flex items-center space-x-4 text-sm text-slate-500">
                      <span><Calendar className="inline h-4 w-4 mr-1" />{parseLocalDate(event.start_date).toLocaleDateString(currentLang)} - {parseLocalDate(event.end_date).toLocaleDateString(currentLang)}</span>
                      <span>{t('dashboard.entry_fee')}: {event.buyer_fee > 0 ? formatPrice(event.buyer_fee) : t('dashboard.free')}</span>
                    </div>
                  </div>
                  {event.status === 'upcoming' ? (
                    <Button variant="outline" disabled>
                      {t('dashboard.coming_soon')}
                    </Button>
                  ) : isRegisteredForEvent(event.id) ? (
                    event.status === 'live' ? (
                      <Button variant="outline" onClick={() => { setSelectedEvent(event); loadExhibitors(event.id); setActiveTab('exhibitors'); }} data-testid={`browse-exhibitors-${event.id}`}>
                        {t('dashboard.browse_exhibitors')} <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        className="text-amber-600 border-amber-300 hover:bg-amber-50"
                        onClick={() => toast.info(t('event_detail.exhibitor_details_live'), { duration: 3000 })}
                        data-testid={`browse-exhibitors-locked-${event.id}`}
                      >
                        <Lock className="h-4 w-4 mr-1" /> {t('event_detail.available_when_live')}
                      </Button>
                    )
                  ) : (
                    <Button onClick={() => { setEventToRegister(event); setShowRegisterModal(true); }}>
                      {t('dashboard.register')}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BuyerEventsTab;
